import { FoundationGroup, ThemeTokenOrder } from '@finxone-platform/shared/sys-config-types';
import type { FoundationTokenRow } from './global-theme-foundation-tokens-form.utils';

/**
 * `names` in the saved `order`: the listed names first, as listed, then the
 * rest in their given order. Stale names in `order` are ignored.
 */
export function orderNames(names: string[], order?: string[]): string[] {
  if (!order?.length) return names;
  const listed = order.filter((name) => names.includes(name));
  return [...new Set([...listed, ...names])];
}

/** A copy of `record` with its keys in the saved `order` (see orderNames). */
export function orderRecord<T>(record: Record<string, T>, order?: string[]): Record<string, T> {
  return Object.fromEntries(orderNames(Object.keys(record), order).map((name) => [name, record[name]]));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The theme form's row order as saved `tokenOrder`: colour names per palette
 * group (not the legacy `customColors` list), typography style names, and
 * foundation token names per scale.
 */
export function toThemeTokenOrder(formValue: {
  generalConfigurations?: { palette?: Record<string, unknown> };
  typography?: Record<string, unknown>;
  foundations?: Partial<Record<FoundationGroup, FoundationTokenRow[]>>;
}): ThemeTokenOrder {
  const palette: Record<string, string[]> = {};
  for (const [group, colours] of Object.entries(formValue.generalConfigurations?.palette ?? {})) {
    if (isRecord(colours)) palette[group] = Object.keys(colours);
  }
  const foundations: Partial<Record<FoundationGroup, string[]>> = {};
  for (const [group, rows] of Object.entries(formValue.foundations ?? {})) {
    foundations[group as FoundationGroup] = (rows ?? []).map((row) => row.name);
  }
  return { palette, typography: Object.keys(formValue.typography ?? {}), foundations };
}
